import { useEffect, useState } from "react";
import { ReceiptText, Package } from "lucide-react";
import { Loader } from "@/components/ui/Loader";
import { PedidoCard, OrderItem, fetchSingleOrderItems } from "@/services/suprimentos";
import { Modal } from "@/components/ui/Modal";
import { formatCurrency, formatNumber, formatDate } from "@/utils/format";
import { cn } from "@/utils/cn";

export function PedidoItensModal({
  pedido,
  onClose,
}: {
  pedido: PedidoCard;
  onClose: () => void;
}) {
  const [items, setItems] = useState<OrderItem[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchSingleOrderItems(pedido.purchaseOrderId)
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [pedido.purchaseOrderId]);

  const totalItens = items?.reduce((s, it) => s + (it.totalAmount ?? 0), 0) ?? 0;

  return (
    <Modal
      open
      onClose={onClose}
      title={
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-brand-50 flex-shrink-0">
            <ReceiptText className="w-5 h-5 text-brand-600" />
          </div>
          <div className="min-w-0">
            <p className="text-base font-bold text-slate-800 leading-tight">
              Pedido #{pedido.orderNumber ?? pedido.purchaseOrderId}
            </p>
            {pedido.supplier && <p className="text-xs text-slate-400 truncate">{pedido.supplier}</p>}
          </div>
        </div>
      }
      size="lg"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-brand-50 rounded-xl px-4 py-3 text-center border border-brand-100">
            <p className="text-xs text-slate-500 mb-1">Valor do Pedido</p>
            <p className="text-base font-bold text-brand-700">{formatCurrency(pedido.value ?? 0)}</p>
          </div>
          <div className="bg-slate-50 rounded-xl px-4 py-3 text-center border border-slate-100">
            <p className="text-xs text-slate-500 mb-1">Data</p>
            <p className="text-base font-bold text-slate-700">
              {pedido.date ? formatDate(pedido.date) : "—"}
            </p>
          </div>
          <div className="bg-slate-50 rounded-xl px-4 py-3 text-center border border-slate-100">
            <p className="text-xs text-slate-500 mb-1">Status</p>
            <p
              className={cn(
                "text-base font-bold",
                pedido.received ? "text-emerald-600" : pedido.hasFinancialGenerated ? "text-amber-600" : "text-red-500"
              )}
            >
              {pedido.received ? "Recebido" : pedido.hasFinancialGenerated ? "Ag. Entrega" : "Pendente"}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-slate-400">
            <Loader size="md" label="Carregando insumos do pedido…" />
          </div>
        ) : !items?.length ? (
          <div className="flex flex-col items-center gap-2 py-10 text-slate-400">
            <Package className="w-8 h-8 opacity-40" />
            <p className="text-sm">Nenhum insumo encontrado para este pedido.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold text-slate-500 uppercase tracking-wide">Cód.</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide">Descrição</th>
                  <th className="text-center px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide">Qtd.</th>
                  <th className="text-center px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide">Un.</th>
                  <th className="text-right px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide">Preço Unit.</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-slate-500 uppercase tracking-wide">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((it, i) => (
                  <tr key={`${it.itemNumber}-${i}`} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-2 text-slate-500 font-mono">{it.code || it.itemNumber || `${i + 1}`}</td>
                    <td className="px-3 py-2 text-slate-700 max-w-[220px]">
                      <span className="block truncate" title={it.description}>
                        {it.description || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center text-slate-600">
                      {it.quantity > 0 ? formatNumber(it.quantity, 2) : "—"}
                    </td>
                    <td className="px-3 py-2 text-center text-slate-400">{it.unit || "—"}</td>
                    <td className="px-3 py-2 text-right text-slate-600">
                      {it.netPrice > 0 || it.unitPrice > 0
                        ? formatCurrency(it.netPrice > 0 ? it.netPrice : it.unitPrice)
                        : "—"}
                    </td>
                    <td className="px-4 py-2 text-right font-semibold text-slate-800">
                      {it.totalAmount > 0 ? formatCurrency(it.totalAmount) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-slate-200 bg-slate-50">
                <tr>
                  <td colSpan={5} className="px-4 py-2 font-semibold text-slate-400 uppercase text-xs">
                    {items.length} insumo{items.length !== 1 ? "s" : ""}
                  </td>
                  <td className="px-4 py-2 text-right font-bold text-slate-800">{formatCurrency(totalItens)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}
